import { useState, type ReactElement } from 'react'
import { useQueryState } from 'nuqs'
import { parseAsUserId } from '@util'
import { ALL_COMPANIES, isDefined, type TaskId, type UserId } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { Loader, Stack, Text } from '@mantine/core'
import { useProfile, useProgramsMap, useTasks, useTranslation } from '@front/model'
import { ButtonRefresh, NotReady, Page, TPushPanel, TSectionList, UserInlineSelect } from '@components'
import { TaskEvidencePanel } from './TaskEvidencePanel'
import { TroubleshootTaskItem } from './TroubleshootTaskItem'

function TaskTroubleshootIndex(): ReactElement {
  const { isTantalim, isLoading } = useProfile()
  const { t } = useTranslation()

  if (isLoading) return <NotReady isLoading />
  if (!isTantalim) return <NotReady error={t('Invalid request')} />

  return <TaskTroubleshootContent />
}

function TaskTroubleshootContent(): ReactElement {
  const { t } = useTranslation()
  const [userId, setUserId] = useQueryState<UserId>('userId', parseAsUserId)

  return (
    <Page>
      <Page.Heading title={t('Tasks Troubleshooting')} />
      <Page.Content noPadding>
        {userId ? (
          <SelectedUserTasks key={userId} userId={userId} onUserChange={setUserId} />
        ) : (
          <Stack className='p-5xl' maw={640} gap='lg'>
            <Text c='dimmed'>Select a user to inspect their current task list.</Text>
            <UserInlineSelect
              allowDeselect={false}
              companyId={ALL_COMPANIES}
              label={t('User')}
              value={undefined}
              onChange={setUserId}
            />
          </Stack>
        )}
      </Page.Content>
    </Page>
  )
}

type SelectedUserTasksProps = { userId: UserId; onUserChange: (userId: UserId) => void }

function SelectedUserTasks({ userId, onUserChange }: SelectedUserTasksProps): ReactElement {
  const { t } = useTranslation()
  const { data, isLoading, error, isFetching, refetch } = useTasks({ ownerId: userId })
  const { data: programs } = useProgramsMap({ impersonateUserId: userId })
  const [selectedId, setSelectedId] = useState<TaskId>()
  const selected = data?.find((task) => task.taskId === selectedId)

  return (
    <div className='flex min-h-0 flex-1 overflow-hidden'>
      <Stack className='px-5xl py-4xl min-w-0 flex-1 overflow-auto' gap='lg'>
        <Stack maw={640} gap='xs'>
          <UserInlineSelect
            allowDeselect={false}
            companyId={ALL_COMPANIES}
            label={t('User')}
            value={userId}
            onChange={onUserChange}
          />
          <Text size='sm' c='dimmed'>
            Tasks in this user’s current task list
          </Text>
        </Stack>
        {isLoading && <Loader />}
        {error && <Text c='red'>{error}</Text>}
        {data && (
          <TSectionList
            heading={{
              title: `${data.length} tasks`,
              children: <ButtonRefresh isFetching={isFetching} refetch={refetch} />,
            }}
          >
            {data.map((task) => (
              <TroubleshootTaskItem
                key={task.taskId}
                task={task}
                programName={task.programId ? programs?.get(task.programId)?.name : undefined}
                selected={selectedId === task.taskId}
                onClick={() => setSelectedId(task.taskId)}
              />
            ))}
          </TSectionList>
        )}
      </Stack>
      <TPushPanel
        opened={isDefined(selected)}
        title={selected?.subject ?? 'Task details'}
        width={640}
        onClose={() => setSelectedId(undefined)}
      >
        {selected && <TaskEvidencePanel ownerId={userId} task={selected} />}
      </TPushPanel>
    </div>
  )
}

export const Route = createFileRoute('/task/troubleshoot/')({ component: TaskTroubleshootIndex })
